/**
 * Funcionários — itens modernos com avatar (rodada visual): nome em texto forte
 * (NÃO link), metadados na linha secundária, badges de status/observações
 * resumidas (texto longo fica em tooltip ou na página de detalhe), clique no
 * item abre o detalhe. Agrupamentos com header discreto + count.
 */
import { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Group, Text, TextInput, Title, Tooltip } from "@mantine/core";
import { IconSearch, IconUserPlus } from "@tabler/icons-react";
import { api } from "../api";
import { fmtData } from "../format";
import { Carregando, Vazio } from "../componentes/Estados";
import { ItemPessoa } from "../componentes/listas";
import { navegar } from "../roteador";

interface DesligamentoResumo {
  dataDesligamento: string;
  tipoTitulo: string | null;
}

interface Funcionario {
  id: string;
  nome: string;
  matriculaDominio: number | null;
  idConvenia: number | null;
  tipoVinculo: string;
  centroCusto: number;
  ativo: boolean;
  observacao: string | null;
  desligamento: DesligamentoResumo | null;
}

/** Badge-resumo da observação (texto completo fica no tooltip — nunca infla a linha). */
function ObsBadge({ observacao }: { observacao: string | null }) {
  if (!observacao) return null;
  const resumo = observacao.length > 34 ? `${observacao.slice(0, 34).trimEnd()}…` : observacao;
  return (
    <Tooltip label={observacao} multiline maw={420} withArrow>
      <Badge variant="outline" color="gray" style={{ cursor: "help" }}>
        obs · {resumo}
      </Badge>
    </Tooltip>
  );
}

export function FuncionariosPage() {
  const [lista, setLista] = useState<Funcionario[] | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [busca, setBusca] = useState("");

  useEffect(() => {
    api
      .get<{ funcionarios: Funcionario[] }>("/funcionarios")
      .then((r) => setLista(r.funcionarios))
      .catch(setErro);
  }, []);

  const grupos = useMemo((): { titulo: string; pessoas: Funcionario[]; badge: (f: Funcionario) => React.ReactNode }[] => {
    const q = busca.trim().toLowerCase();
    const filtra = (f: Funcionario) =>
      !q ||
      f.nome.toLowerCase().includes(q) ||
      String(f.matriculaDominio ?? "").includes(q) ||
      String(f.idConvenia ?? "").includes(q) ||
      (f.observacao ?? "").toLowerCase().includes(q);
    const base = (lista ?? []).filter(filtra);
    const ehAfastada = (f: Funcionario) => !!f.observacao?.toLowerCase().includes("afastada");
    return [
      {
        titulo: "CLT ativos",
        pessoas: base.filter((f) => f.tipoVinculo === "clt" && f.ativo && !ehAfastada(f)),
        badge: (f) => <ObsBadge observacao={f.observacao} />,
      },
      {
        titulo: "Afastada (contrato suspenso)",
        pessoas: base.filter((f) => f.tipoVinculo === "clt" && f.ativo && ehAfastada(f)),
        badge: () => (
          <Badge color="yellow" variant="light">
            suspensa (INSS)
          </Badge>
        ),
      },
      {
        titulo: "Sócios ativos (pró-labore)",
        pessoas: base.filter((f) => f.tipoVinculo === "socio" && f.ativo),
        badge: (f) => (
          <>
            <Badge color="sillis" variant="light">
              pró-labore
            </Badge>
            <ObsBadge observacao={f.observacao} />
          </>
        ),
      },
      {
        titulo: "Sócios pendentes de cadastro",
        pessoas: base.filter((f) => f.tipoVinculo === "socio" && !f.ativo),
        badge: () => (
          <Badge color="red" variant="light">
            sem chaves Domínio/Convenia/CPF
          </Badge>
        ),
      },
      {
        titulo: "Desligados (CLT inativos)",
        pessoas: base.filter((f) => f.tipoVinculo === "clt" && !f.ativo),
        badge: (f) =>
          f.desligamento ? (
            <Tooltip
              label={`${f.desligamento.tipoTitulo ?? "Desligamento"} — espelho Convenia (detalhe na ficha, aba Contrato)`}
              multiline
              maw={360}
              withArrow
            >
              <Badge color="gray" variant="light" style={{ cursor: "help" }}>
                desligado em {fmtData(f.desligamento.dataDesligamento)}
              </Badge>
            </Tooltip>
          ) : (
            <Badge color="gray" variant="outline">
              inativo
            </Badge>
          ),
      },
    ];
  }, [lista, busca]);

  return (
    <>
      <Group justify="space-between" mb={4}>
        <Title order={2}>Funcionários</Title>
        <Button component="a" href="#/funcionarios/novo" leftSection={<IconUserPlus size={16} />}>
          Admitir funcionário
        </Button>
      </Group>
      <Text size="sm" c="dimmed" mb="md">
        Pessoas da folha — clique para abrir o detalhe (contrato, benefícios, dependentes, banco de horas).
      </Text>
      {erro != null && (
        <Alert color="red" title="Falha ao carregar" mb="sm">
          {String(erro)}
        </Alert>
      )}
      <TextInput
        placeholder="buscar por nome, chave ou observação…"
        leftSection={<IconSearch size={14} />}
        value={busca}
        onChange={(e) => setBusca(e.currentTarget.value)}
        w={360}
        mb="md"
        aria-label="Buscar funcionário"
      />
      {!lista ? (
        <Carregando linhas={5} />
      ) : (
        grupos.map(({ titulo, pessoas, badge }) => (
          <div key={titulo} style={{ marginBottom: 18 }}>
            <Group gap="xs" mb={6}>
              <Text size="xs" fw={700} tt="uppercase" c="dimmed" style={{ letterSpacing: 0.4 }}>
                {titulo}
              </Text>
              <Badge variant="light" color="gray" size="sm">
                {pessoas.length}
              </Badge>
            </Group>
            {pessoas.length === 0 ? (
              <Vazio texto="Ninguém neste grupo com a busca atual." />
            ) : (
              pessoas.map((f) => (
                <ItemPessoa
                  key={f.id}
                  funcionarioId={f.id}
                  nome={f.nome}
                  opaco={!f.ativo}
                  linhaSecundaria={`Domínio ${f.matriculaDominio ?? "—"} · Convenia ${f.idConvenia ?? "—"}`}
                  badges={badge(f)}
                  onClick={() => navegar(`/funcionarios/${f.id}`)}
                />
              ))
            )}
          </div>
        ))
      )}
    </>
  );
}
